import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '../../../services/api';

/**
 * El estado de la cola del despachador.
 *
 * Es la pantalla que convierte «hay 600 tareas de WhatsApp paradas» en algo
 * accionable: qué espera, por qué, y cuándo se desatasca. Sin ella, una cola
 * atascada solo se puede depurar consultando Mongo a mano.
 *
 * Los tres números de arriba son tres cosas DISTINTAS y conviene no leerlos
 * como un total:
 *
 *   Listas       ya selladas, esperando a que el agente las procese.
 *   Esperando    retenidas, cada una con su motivo.
 *   Sin mirar    entran en la próxima pasada del despachador.
 */

const CANAL_ETIQUETA = { maytapi: 'Maytapi', navegador: 'Navegador' };

const cuando = (fecha) => {
  if (!fecha) return null;
  const d = new Date(fecha);
  const hoy = new Date();
  const mismoDia = d.toDateString() === hoy.toDateString();
  return d.toLocaleString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    ...(mismoDia ? {} : { day: '2-digit', month: 'short' }),
  });
};

// Las clases van literales, no interpoladas: Tailwind purga lo que no encuentra
// escrito tal cual en el código, así que un `text-${tono}-900` se quedaría sin
// color en producción y con color en desarrollo.
const Cifra = ({ valor, titulo, detalle }) => (
  <div className='flex-1 px-6 py-4'>
    <p className='text-2xl font-semibold tabular-nums text-slate-900'>{valor}</p>
    <p className='text-sm font-medium text-slate-700 mt-0.5'>{titulo}</p>
    <p className='text-xs text-slate-500 mt-0.5'>{detalle}</p>
  </div>
);

const DispatchQueue = () => {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get('/wa-dispatch/queue');
      setDatos(data.data);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  if (cargando && !datos) {
    return (
      <div className='bg-white rounded-lg border border-slate-200 px-6 py-8 mb-6'>
        <p className='text-sm text-slate-500'>Cargando la cola…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='bg-white rounded-lg border border-red-200 px-6 py-4 mb-6'>
        <p className='text-sm text-red-700'>{error}</p>
      </div>
    );
  }

  const { resumen, motivos, por_linea: porLinea } = datos;

  return (
    <div className='bg-white rounded-lg border border-slate-200 overflow-hidden mb-6'>
      <div className='px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-start justify-between gap-4'>
        <div>
          <h3 className='text-lg font-medium text-slate-900'>Cola de envío</h3>
          <p className='text-sm text-slate-500 mt-1'>
            Qué está esperando para salir por WhatsApp, y por qué
          </p>
        </div>
        <button
          type='button'
          onClick={cargar}
          className='inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900'
        >
          <ArrowPathIcon className={`w-4 h-4 ${cargando ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      <div className='flex divide-x divide-slate-200 border-b border-slate-200'>
        <Cifra
          valor={resumen.selladas}
          titulo='Listas'
          detalle='Con canal y línea asignados'
        />
        <Cifra
          valor={resumen.retenidas}
          titulo='Esperando'
          detalle='Retenidas, con motivo'
        />
        <Cifra
          valor={resumen.sin_mirar}
          titulo='Sin mirar'
          detalle='Entran en la próxima pasada'
        />
      </div>

      {motivos.length > 0 && (
        <div className='px-6 py-4 border-b border-slate-200'>
          <h4 className='text-sm font-medium text-slate-900 mb-3 flex items-center gap-1.5'>
            <ExclamationTriangleIcon className='w-4 h-4 text-amber-500' />
            Por qué esperan
          </h4>
          <ul className='space-y-2'>
            {motivos.map((m) => (
              <li
                key={m.motivo}
                className='flex items-baseline justify-between gap-4 text-sm'
              >
                <span className='text-slate-700 break-words'>{m.motivo}</span>
                <span className='flex items-baseline gap-3 flex-shrink-0'>
                  {m.proximo_intento && (
                    <span className='text-xs text-slate-500 inline-flex items-center gap-1'>
                      <ClockIcon className='w-3.5 h-3.5' />
                      {cuando(m.proximo_intento)}
                    </span>
                  )}
                  <span className='font-medium tabular-nums text-slate-900'>{m.total}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {porLinea.length > 0 && (
        <div className='px-6 py-4'>
          <h4 className='text-sm font-medium text-slate-900 mb-3'>Listas, por línea</h4>
          <ul className='space-y-2'>
            {porLinea.map((l) => (
              <li
                key={`${l.canal}-${l.phone_id}`}
                className='flex items-baseline justify-between gap-4 text-sm'
              >
                <span className='text-slate-700'>
                  {l.phone_id || <span className='text-slate-400'>sin línea fija</span>}
                  <span className='text-slate-400'> · {CANAL_ETIQUETA[l.canal] || l.canal}</span>
                </span>
                <span className='font-medium tabular-nums text-slate-900'>{l.total}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {motivos.length === 0 && porLinea.length === 0 && (
        <div className='px-6 py-6'>
          <p className='text-sm text-slate-500'>
            No hay nada en cola. Todo lo pendiente ya ha salido o todavía no ha vencido.
          </p>
        </div>
      )}
    </div>
  );
};

export default DispatchQueue;
